import {
  helloEventSchema,
  invalidateEventSchema,
  metadataEventSchema,
  pendingEventSchema,
} from '@ofmos/shared/sse';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useAppClient } from '../ClientProvider';
import {
  type CacheOperation,
  cancelRefetchFor,
  metadataInvalidations,
  pendingInvalidations,
  recordInvalidations,
  resyncInvalidations,
} from './invalidation';

/**
 * LIVE UPDATES: `GET /api/events` -> TanStack invalidation.
 *
 * Mounted once, inside the authenticated shell. Not in main.tsx: an unauthenticated visitor
 * would open a stream that can only 401, and a login screen has nothing to invalidate.
 *
 * Why `EventSource` alone is not enough:
 *  - its built-in reconnect has no backoff, so a server that is down gets hammered once per
 *    second per open tab, and a 401 (an expired session) retries forever;
 *  - iOS suspends a backgrounded PWA. The stream comes back dead, and the browser fires no
 *    error until something is written to it.
 *
 * So the stream is closed on any error and reopened with exponential backoff plus jitter, and
 * `visibilitychange` is a first-class signal: returning to the tab resyncs immediately and
 * reconnects without waiting out the backoff. Whatever the server sent while we were away
 * arrived nowhere, which is why a reconnect resyncs rather than trusting the cache.
 */

const FIRST_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

/**
 * The shape of a zod schema, structurally. `zod` itself is a dependency of `shared`, not
 * of the browser bundle, so this file names what it needs instead of importing the library
 * only to spell one type.
 */
interface Validator<T> {
  safeParse(input: unknown): { success: true; data: T } | { success: false };
}

/** Reads one SSE frame. A frame that does not match the shared schema is ignored. */
function payload<T>(schema: Validator<T>, event: Event): T | null {
  const data = (event as MessageEvent<string>).data;
  if (typeof data !== 'string') return null;
  try {
    const parsed = schema.safeParse(JSON.parse(data));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export interface LiveUpdateOptions {
  /** Off while nobody is signed in. */
  readonly enabled: boolean;
  readonly path?: string;
}

export function useLiveUpdates({ enabled, path = '/api/events' }: LiveUpdateOptions): void {
  const queryClient = useQueryClient();
  const client = useAppClient();
  /** Last metadata version seen, so a reconnect can tell whether the model moved. */
  const metadataVersion = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || typeof EventSource !== 'function') return;

    let source: EventSource | null = null;
    let retryDelay = FIRST_RETRY_MS;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    /**
     * A FRAME NEVER CANCELS A FETCH THE USER STARTED (`table-completion.md` D25).
     *
     * The second argument is the whole of it: without it `invalidateQueries` inherits
     * `cancelRefetch: true` and silently kills an in-flight `fetchNextPage` on a record
     * list, and the writing tab gets its own frame (the hub's fan-out has no origin
     * exclusion), so pressing `Show more` while anybody saves any record in that object
     * loads nothing and says nothing. `cancelRefetchFor` owns which prefixes that applies
     * to, so this call site cannot drift from the reconciler's or the mutations'.
     */
    const apply = (operations: readonly CacheOperation[]): void => {
      for (const operation of operations) {
        void queryClient.invalidateQueries(
          { queryKey: operation.queryKey, refetchType: operation.refetch },
          { cancelRefetch: cancelRefetchFor(operation.queryKey) },
        );
      }
    };

    const connect = (): void => {
      if (stopped) return;
      source = new EventSource(path, { withCredentials: true });

      source.addEventListener('open', () => {
        retryDelay = FIRST_RETRY_MS;
      });

      source.addEventListener('hello', (event) => {
        const hello = payload(helloEventSchema, event);
        if (hello === null) return;
        const reconnected = metadataVersion.current !== null;
        const modelMoved = metadataVersion.current !== hello.metadataVersion;
        metadataVersion.current = hello.metadataVersion;
        // The client caches the metadata graph; a new version makes that cache wrong.
        if (modelMoved) client.metadata.refresh();
        if (reconnected) apply(resyncInvalidations());
      });

      source.addEventListener('invalidate', (event) => {
        const notice = payload(invalidateEventSchema, event);
        if (notice !== null) apply(recordInvalidations(notice.objectIds));
      });

      source.addEventListener('pending', (event) => {
        // INV-10: the notice carries ids only, so the queue and the badge are refetched.
        if (payload(pendingEventSchema, event) !== null) apply(pendingInvalidations());
      });

      source.addEventListener('metadata', (event) => {
        const notice = payload(metadataEventSchema, event);
        if (notice === null) return;
        metadataVersion.current = notice.version;
        client.metadata.refresh();
        apply(metadataInvalidations(notice.objectIds));
      });

      source.addEventListener('error', () => {
        source?.close();
        source = null;
        if (stopped) return;
        retryTimer = setTimeout(connect, retryDelay + Math.random() * 250);
        retryDelay = Math.min(retryDelay * 2, MAX_RETRY_MS);
      });
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible' || stopped) return;
      apply(resyncInvalidations());
      if (source !== null) return;
      clearTimeout(retryTimer);
      retryDelay = FIRST_RETRY_MS;
      connect();
    };

    connect();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopped = true;
      clearTimeout(retryTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      source?.close();
    };
  }, [enabled, path, queryClient, client]);
}
